import { api } from '@/services/api'

export type UploadScope = 'product' | 'service' | 'review'

interface PresignResponse {
  uploadUrl: string
  publicUrl: string
  key: string
  expiresIn: number
  headers: Record<string, string>
}

/**
 * Upload a single image to S3 via the API-issued presigned URL.
 *
 * The server never proxies the file bytes:
 *   1. POST /uploads/presign → server returns a 5-min PUT URL + headers
 *   2. PUT the bytes directly to S3 with those headers
 *   3. Return the durable `publicUrl` for storage in Product/Service/Review
 */
export async function uploadImage(file: File, scope: UploadScope): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Type de fichier non supporté (image uniquement)')
  }

  const presign = await api.post<{ data: PresignResponse }>('/uploads/presign', {
    scope,
    contentType: file.type,
    contentLength: file.size,
  })
  const { uploadUrl, publicUrl, headers } = presign.data.data

  const put = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers,
  })
  if (!put.ok) {
    const text = await put.text().catch(() => '')
    throw new Error(`Upload S3 échoué (${String(put.status)}): ${text.slice(0, 200)}`)
  }
  return publicUrl
}
